/**
 * Resource API wrappers. Centralizes URL paths and query params.
 */

import {api, ApiError} from './api';
import type {
  BriefResponse,
  BriefUpsertRequest,
  ConfirmPlanRequest,
  Customer,
  CustomerCreate,
  CustomerUpdate,
  DashboardStats,
  DeploymentJob,
  DeploymentJobCreateResponse,
  Domain,
  DomainCheckResult,
  DomainCreate,
  DomainListResponse,
  DomainUpdate,
  EditorBrandingPayload,
  EditorFooterPayload,
  EditorNavigationPayload,
  EditorPage,
  EditorPageCreate,
  EditorPageUpdate,
  EditorPublishResponse,
  AiGenerateRequest,
  AiGenerateResponse,
  EditorSection,
  EditorSectionCreate,
  EditorSectionSchemaResponse,
  EditorSectionUpdate,
  EditorSnapshot,
  GeneratePlanRequest,
  GeneratePlanResponse,
  GenerateSiteRequest,
  GenerateSiteResponse,
  PublishCreateRequest,
  PublishCreateResponse,
  ReconcileResponse,
  Server,
  ServerCheck,
  ServerCreate,
  ServerUpdate,
  Site,
  SiteCreate,
  SiteJobListResponse,
  SiteJobStatusResponse,
  SiteUpdate,
  ListResponse,
  Template,
  TemplateApplyRequest,
  TemplateApplyResponse,
  TemplateHistoryResponse,
  TemplateListResponse,
  TemplateStatusResponse,
  AgentApproveResponse,
  AgentCommandResponse,
  AgentContext,
  AgentModifyResponse,
  AgentPreviewResponse,
  AgentPublishResponse,
  AgentRollbackResponse,
  AgentVersionResponse,
  CatalogIngestResponse,
  CatalogProductsResponse,
  VisualCriticReport,
  ActiveModelSetupRequest,
  ModelConfigResponse,
  ModelRuntimeConfig,
} from './types';

export interface ListCustomersParams {
  page?: number;
  page_size?: number;
  status?: string;
  search?: string;
}

export const customersApi = {
  list: (p: ListCustomersParams = {}) => {
    const qs = new URLSearchParams();
    if (p.page) qs.set('page', String(p.page));
    if (p.page_size) qs.set('page_size', String(p.page_size));
    if (p.status) qs.set('status', p.status);
    if (p.search) qs.set('search', p.search);
    const q = qs.toString();
    return api.get<ListResponse<Customer>>(`/api/v1/customers${q ? '?' + q : ''}`);
  },
  get: (id: string) => api.get<Customer>(`/api/v1/customers/${id}`),
  create: (body: CustomerCreate) => api.post<Customer>('/api/v1/customers', body),
  update: (id: string, body: CustomerUpdate) =>
    api.patch<Customer>(`/api/v1/customers/${id}`, body),
  delete: (id: string) => api.delete<void>(`/api/v1/customers/${id}`),
};

export interface ListServersParams {
  page?: number;
  page_size?: number;
  ownership?: string;
  management_mode?: string;
  customer_id?: string;
  status?: string;
  search?: string;
}

export const serversApi = {
  list: (p: ListServersParams = {}) => {
    const qs = new URLSearchParams();
    if (p.page) qs.set('page', String(p.page));
    if (p.page_size) qs.set('page_size', String(p.page_size));
    if (p.ownership) qs.set('ownership', p.ownership);
    if (p.management_mode) qs.set('management_mode', p.management_mode);
    if (p.customer_id) qs.set('customer_id', p.customer_id);
    if (p.status) qs.set('status', p.status);
    if (p.search) qs.set('search', p.search);
    const q = qs.toString();
    return api.get<ListResponse<Server>>(`/api/v1/servers${q ? '?' + q : ''}`);
  },
  get: (id: string) => api.get<Server>(`/api/v1/servers/${id}`),
  create: (body: ServerCreate) => api.post<Server>('/api/v1/servers', body),
  update: (id: string, body: ServerUpdate) =>
    api.patch<Server>(`/api/v1/servers/${id}`, body),
  delete: (id: string) => api.delete<void>(`/api/v1/servers/${id}`),
  // Phase 2
  check: (id: string) => api.post<ServerCheck>(`/api/v1/servers/${id}/check`),
  checks: (id: string, p: {page?: number; page_size?: number} = {}) => {
    const qs = new URLSearchParams();
    if (p.page) qs.set('page', String(p.page));
    if (p.page_size) qs.set('page_size', String(p.page_size));
    const q = qs.toString();
    return api.get<ListResponse<ServerCheck>>(
      `/api/v1/servers/${id}/checks${q ? '?' + q : ''}`
    );
  },
};

export interface ListSitesParams {
  page?: number;
  page_size?: number;
  customer_id?: string;
  server_id?: string;
  hosting_mode?: string;
  status?: string;
  search?: string;
}

export const sitesApi = {
  list: (p: ListSitesParams = {}) => {
    const qs = new URLSearchParams();
    if (p.page) qs.set('page', String(p.page));
    if (p.page_size) qs.set('page_size', String(p.page_size));
    if (p.customer_id) qs.set('customer_id', p.customer_id);
    if (p.server_id) qs.set('server_id', p.server_id);
    if (p.hosting_mode) qs.set('hosting_mode', p.hosting_mode);
    if (p.status) qs.set('status', p.status);
    if (p.search) qs.set('search', p.search);
    const q = qs.toString();
    return api.get<ListResponse<Site>>(`/api/v1/sites${q ? '?' + q : ''}`);
  },
  get: (id: string) => api.get<Site>(`/api/v1/sites/${id}`),
  create: (body: SiteCreate) => api.post<Site>('/api/v1/sites', body),
  update: (id: string, body: SiteUpdate) => api.patch<Site>(`/api/v1/sites/${id}`, body),
  delete: (id: string) => api.delete<void>(`/api/v1/sites/${id}`),
  // Phase 3
  deploy: (id: string) =>
    api.post<DeploymentJobCreateResponse>(`/api/v1/sites/${id}/deploy`, {}),
  deployments: (id: string, p: {page?: number; page_size?: number} = {}) => {
    const qs = new URLSearchParams();
    if (p.page) qs.set('page', String(p.page));
    if (p.page_size) qs.set('page_size', String(p.page_size));
    const q = qs.toString();
    return api.get<ListResponse<DeploymentJob>>(
      `/api/v1/sites/${id}/deployments${q ? '?' + q : ''}`
    );
  },
  // Phase 4
  publish: (id: string, body: PublishCreateRequest = {}) =>
    api.post<PublishCreateResponse>(`/api/v1/sites/${id}/publish`, body),
  // Phase 7: structured site editor
  editor: {
    snapshot: (id: string) => api.get<EditorSnapshot>(`/api/v1/sites/${id}/editor`),
    sectionsSchema: (id: string) =>
      api.get<EditorSectionSchemaResponse>(
        `/api/v1/sites/${id}/editor/sections-schema`
      ),
    createPage: (id: string, body: EditorPageCreate, expected: number) =>
      api.post<EditorPage>(`/api/v1/sites/${id}/editor/pages`, body, {
        params: {expected_revision: expected},
      }),
    updatePage: (
      id: string,
      pageId: string,
      body: EditorPageUpdate,
      expected: number
    ) =>
      api.patch<EditorPage>(
        `/api/v1/sites/${id}/editor/pages/${pageId}`,
        body,
        {params: {expected_revision: expected}}
      ),
    deletePage: (id: string, pageId: string, expected: number) =>
      api.delete<{deleted: boolean; page_id: string}>(
        `/api/v1/sites/${id}/editor/pages/${pageId}`,
        {params: {expected_revision: expected}}
      ),
    reorderPages: (id: string, pageIds: string[], expected: number) =>
      api.post<{pages: EditorPage[]}>(
        `/api/v1/sites/${id}/editor/pages/reorder`,
        {page_ids_in_order: pageIds, expected_revision: expected}
      ),
    createSection: (
      id: string,
      pageId: string,
      body: EditorSectionCreate,
      expected: number
    ) =>
      api.post<EditorSection>(`/api/v1/sites/${id}/editor/sections`, body, {
        params: {page_id: pageId, expected_revision: expected},
      }),
    updateSection: (
      id: string,
      sectionId: string,
      body: EditorSectionUpdate,
      expected: number
    ) =>
      api.patch<EditorSection>(
        `/api/v1/sites/${id}/editor/sections/${sectionId}`,
        body,
        {params: {expected_revision: expected}}
      ),
    deleteSection: (id: string, sectionId: string, expected: number) =>
      api.delete<{deleted: boolean; section_id: string}>(
        `/api/v1/sites/${id}/editor/sections/${sectionId}`,
        {params: {expected_revision: expected}}
      ),
    reorderSections: (id: string, sectionIds: string[], expected: number) =>
      api.post<{sections: EditorSection[]}>(
        `/api/v1/sites/${id}/editor/sections/reorder`,
        {section_ids_in_order: sectionIds, expected_revision: expected}
      ),
    saveBranding: (id: string, body: EditorBrandingPayload, expected: number) =>
      api.patch<unknown>(`/api/v1/sites/${id}/editor/branding`, body, {
        params: {expected_revision: expected},
      }),
    saveNavigation: (id: string, body: EditorNavigationPayload, expected: number) =>
      api.patch<unknown>(`/api/v1/sites/${id}/editor/navigation`, body, {
        params: {expected_revision: expected},
      }),
    saveFooter: (id: string, body: EditorFooterPayload, expected: number) =>
      api.patch<unknown>(`/api/v1/sites/${id}/editor/footer`, body, {
        params: {expected_revision: expected},
      }),
    publish: (id: string) =>
      api.post<EditorPublishResponse>(`/api/v1/sites/${id}/editor/publish`, {}),
    previewToken: (id: string) =>
      api.get<{token: string; expires_at: number; draft_revision: number}>(
        `/api/v1/sites/${id}/editor/preview-token`
      ),
    // Phase 8: Generate with AI on a section. Returns the new
    // section envelope + draft row + generation meta. The backend
    // bumps draft_revision on success and writes a SUCCESS row to
    // section_ai_generations; on failure the Draft is untouched and
    // a FAILED row is written.
    aiGenerate: (
      id: string,
      sectionId: string,
      body: AiGenerateRequest,
      expected: number
    ) =>
      api.post<AiGenerateResponse>(
        `/api/v1/sites/${id}/editor/sections/${sectionId}/ai-generate`,
        body,
        {params: {expected_revision: expected}}
      ),
  },
};

// ===== Phase 5 site content (branding/nav/footer/media) =====
export interface SiteMediaItem {
  attachment_id: number;
  url: string;
  title: string;
  alt: string;
  mime_type: string;
  filesize: number;
  date: string;
}

export interface SiteMediaListResponse {
  items: SiteMediaItem[];
  count: number;
  per_page: number;
}

export const siteContentApi = {
  listMedia: (id: string, params: {per_page?: number; media_type?: string} = {}) => {
    const qs = new URLSearchParams();
    if (params.per_page) qs.set('per_page', String(params.per_page));
    if (params.media_type) qs.set('media_type', params.media_type);
    const q = qs.toString();
    return api.get<SiteMediaListResponse>(
      `/api/v1/sites/${id}/content/media${q ? '?' + q : ''}`
    );
  },
  uploadMedia: (id: string, file: File, altText?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (altText) fd.append('alt_text', altText);
    return fetch(`/api/v1/sites/${id}/content/media`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('wsb_token') || ''}`,
      },
      body: fd,
    }).then(async (r) => {
      const text = await r.text();
      let data: unknown = null;
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
      }
      if (!r.ok) {
        let code = 'HTTP_ERROR';
        let message = `请求失败（HTTP ${r.status}）`;
        if (data && typeof data === 'object') {
          const body = data as {detail?: unknown; error_code?: string; code?: string; message?: string};
          const detail = body.detail;
          if (detail && typeof detail === 'object') {
            const d = detail as {error_code?: string; code?: string; message?: string};
            code = d.error_code || d.code || code;
            message = d.message || message;
          } else if (typeof detail === 'string') {
            message = detail;
          } else {
            code = body.error_code || body.code || code;
            message = body.message || message;
          }
        }
        throw new ApiError(r.status, code, message, data);
      }
      return data as {attachment_id: number; url: string; mime_type: string; filesize: number};
    });
  },
};

// ===== Phase 10A: Site Media Asset lifecycle =====

import type {
  SiteMediaAsset,
  SiteMediaAssetStatus,
  ListSiteMediaAssetsResponse,
  GenerateSiteMediaAssetRequest,
  SelectSiteMediaAssetRequest,
  RegisterWpMediaRequest,
  PublishSiteMediaAssetResponse,
  SiteMediaRole,
  SiteMediaStatus,
} from './types';

export const mediaAssetsApi = {
  list: (
    siteId: string,
    params: {
      role: SiteMediaRole;
      page_id?: string | null;
      section_id?: string | null;
      status?: SiteMediaStatus | null;
    },
  ) => {
    const qs = new URLSearchParams();
    qs.set('role', params.role);
    if (params.page_id) qs.set('page_id', params.page_id);
    if (params.section_id) qs.set('section_id', params.section_id);
    if (params.status) qs.set('status', params.status);
    return api.get<ListSiteMediaAssetsResponse>(
      `/api/v1/sites/${siteId}/media-assets?${qs.toString()}`,
    );
  },
  get: (siteId: string, assetId: string) =>
    api.get<SiteMediaAsset>(`/api/v1/sites/${siteId}/media-assets/${assetId}`),
  getStatus: (siteId: string, assetId: string) =>
    api.get<SiteMediaAssetStatus>(
      `/api/v1/sites/${siteId}/media-assets/${assetId}/status`,
    ),
  generate: (siteId: string, body: GenerateSiteMediaAssetRequest) =>
    api.post<SiteMediaAsset>(`/api/v1/sites/${siteId}/media-assets/generate`, body),
  select: (
    siteId: string,
    assetId: string,
    body: SelectSiteMediaAssetRequest = {},
  ) =>
    api.post<SiteMediaAsset>(
      `/api/v1/sites/${siteId}/media-assets/${assetId}/select`,
      body,
    ),
  deselect: (siteId: string, assetId: string) =>
    api.post<SiteMediaAsset>(
      `/api/v1/sites/${siteId}/media-assets/${assetId}/deselect`,
      {},
    ),
  registerFromWpMedia: (siteId: string, body: RegisterWpMediaRequest) =>
    api.post<SiteMediaAsset>(
      `/api/v1/sites/${siteId}/media-assets/from-wp-media`,
      body,
    ),
  publish: (siteId: string, assetId: string) =>
    api.post<PublishSiteMediaAssetResponse>(
      `/api/v1/sites/${siteId}/media-assets/${assetId}/publish`,
      {},
    ),
};


// ===== Phase 4: Domains =====
export const domainsApi = {
  list: (siteId: string, p: {page?: number; page_size?: number} = {}) => {
    const qs = new URLSearchParams();
    if (p.page) qs.set('page', String(p.page));
    if (p.page_size) qs.set('page_size', String(p.page_size));
    const q = qs.toString();
    return api.get<DomainListResponse>(
      `/api/v1/sites/${siteId}/domains${q ? '?' + q : ''}`
    );
  },
  get: (id: string) => api.get<Domain>(`/api/v1/domains/${id}`),
  create: (siteId: string, body: DomainCreate) =>
    api.post<Domain>(`/api/v1/sites/${siteId}/domains`, body),
  update: (id: string, body: DomainUpdate) =>
    api.patch<Domain>(`/api/v1/domains/${id}`, body),
  delete: (id: string) => api.delete<void>(`/api/v1/domains/${id}`),
  checkDns: (siteId: string, id: string) =>
    api.post<DomainCheckResult>(`/api/v1/sites/${siteId}/domains/${id}/check`, {}),
};

export const deploymentsApi = {
  get: (jobId: string) =>
    api.get<DeploymentJob>(`/api/v1/deployments/${jobId}`),
  // Phase 6.5: reconcile a stale/FAILED job against ground truth
  // (the Site Agent /verify endpoint). Returns 200 with the
  // classification + the new job/site state.
  reconcile: (jobId: string) =>
    api.post<ReconcileResponse>(`/api/v1/deployments/${jobId}/reconcile`, {}),
};

export const statsApi = {
  dashboard: () => api.get<DashboardStats>('/api/v1/stats/dashboard'),
};

// ===== Phase 6: Templates =====
// Re-export the Template-related types so consumers can import them
// from the same module as the API wrappers.
export type {
  Template,
  TemplateApplyRequest,
  TemplateApplyResponse,
  TemplateHistoryItem,
  TemplateHistoryResponse,
  TemplateListResponse,
  TemplateStatusResponse,
} from './types';

export const templatesApi = {
  list: () => api.get<TemplateListResponse>('/api/v1/templates'),
  listAll: () => api.get<TemplateListResponse>('/api/v1/templates/all'),
  get: (templateId: string) => api.get<Template>(`/api/v1/templates/${templateId}`),
  getVersion: (templateId: string, version: string) =>
    api.get<Template>(`/api/v1/templates/${templateId}/${version}`),
  refresh: () => api.post<TemplateListResponse>('/api/v1/templates/refresh', {}),
};

export const siteTemplateApi = {
  apply: (siteId: string, body: TemplateApplyRequest) =>
    api.post<TemplateApplyResponse>(`/api/v1/sites/${siteId}/template/apply`, body),
  status: (siteId: string) =>
    api.get<TemplateStatusResponse>(`/api/v1/sites/${siteId}/template`),
  history: (siteId: string) =>
    api.get<TemplateHistoryResponse>(`/api/v1/sites/${siteId}/template/history`),
};

// ===== Phase 9: AI Site Brief → Full Site Draft =====
export const aiSiteApi = {
  getBrief: (siteId: string) =>
    api.get<BriefResponse>(`/api/v1/sites/${siteId}/brief`),
  upsertBrief: (siteId: string, body: BriefUpsertRequest) =>
    api.post<BriefResponse>(`/api/v1/sites/${siteId}/brief`, body),
  generatePlan: (siteId: string, body: GeneratePlanRequest) =>
    api.post<GeneratePlanResponse>(`/api/v1/sites/${siteId}/ai-site/plan`, body),
  lastPlan: (siteId: string) =>
    api.get<GeneratePlanResponse>(`/api/v1/sites/${siteId}/ai-site/plan`),
  confirmPlan: (siteId: string, body: ConfirmPlanRequest) =>
    api.post<{
      site_id: string;
      // Phase 9.1: the confirm response now includes the just-bumped
      // site-level draft revision. The wizard reads this to refresh
      // its local state — without it the next /ai-site/generate call
      // lands in 409 because the previous revision is stale.
      draft_revision: number;
      pages: Array<{
        id: string;
        role: string;
        slug: string;
        title: string;
        sort_order: number;
        draft_revision: number;
      }>;
    }>(`/api/v1/sites/${siteId}/ai-site/plan/confirm`, body),
  generateFullSite: (siteId: string, body: GenerateSiteRequest) =>
    api.post<GenerateSiteResponse>(`/api/v1/sites/${siteId}/ai-site/generate`, body),
  listJobs: (siteId: string) =>
    api.get<SiteJobListResponse>(`/api/v1/sites/${siteId}/ai-site/jobs`),
  getJob: (siteId: string, jobId: string) =>
    api.get<SiteJobStatusResponse>(`/api/v1/sites/${siteId}/ai-site/jobs/${jobId}`),
  cancelJob: (siteId: string, jobId: string) =>
    api.post<SiteJobStatusResponse>(
      `/api/v1/sites/${siteId}/ai-site/jobs/${jobId}/cancel`,
      {}
    ),
  retryJob: (siteId: string, jobId: string) =>
    api.post<GenerateSiteResponse>(
      `/api/v1/sites/${siteId}/ai-site/jobs/${jobId}/retry`,
      {}
  ),
};

// ===== Agent-first customer workbench =====
export const agentApi = {
  context: (siteId: string) =>
    api.get<AgentContext>(`/api/v1/sites/${siteId}/agent/context`),
  command: (siteId: string, body: {command: string; action?: string}) =>
    api.post<AgentCommandResponse>(`/api/v1/sites/${siteId}/agent/command`, body),
  visualCritic: (siteId: string, body: {viewport?: string; note?: string} = {}) =>
    api.post<VisualCriticReport>(`/api/v1/sites/${siteId}/agent/visual-critic`, body),
  modify: (siteId: string, body: {command: string; expected_revision: number}) =>
    api.post<AgentModifyResponse>(`/api/v1/sites/${siteId}/agent/modify`, body),
  saveVersion: (siteId: string, body: {label: string; visual_report_id?: string | null}) =>
    api.post<AgentVersionResponse>(`/api/v1/sites/${siteId}/agent/version`, body),
  approve: (
    siteId: string,
    body: {expected_revision: number; visual_report_id: string},
  ) => api.post<AgentApproveResponse>(`/api/v1/sites/${siteId}/agent/approve`, body),
  preview: (siteId: string) =>
    api.post<AgentPreviewResponse>(`/api/v1/sites/${siteId}/agent/preview`, {}),
  approvePreview: (
    siteId: string,
    body: {release_id: string; expected_revision: number},
  ) => api.post<AgentPreviewResponse>(`/api/v1/sites/${siteId}/agent/preview/approve`, body),
  publish: (siteId: string, body: {release_id: string}) =>
    api.post<AgentPublishResponse>(`/api/v1/sites/${siteId}/agent/publish`, body),
  rollback: (
    siteId: string,
    body: {job_id: string; expected_current_version: number; confirm: boolean},
  ) => api.post<AgentRollbackResponse>(`/api/v1/sites/${siteId}/agent/rollback`, body),
  executeVisualPlan: (siteId: string) =>
    api.post<{site_id: string; status: string; plan: Record<string, unknown>; ready_count: number}>(
      `/api/v1/sites/${siteId}/agent/visual-plan/execute`,
      {}
    ),
  approveVisualAsset: (siteId: string, assetId: string) =>
    api.post<{site_id: string; asset_id: string; status: string; message: string}>(
      `/api/v1/sites/${siteId}/agent/visual-assets/${assetId}/approve`,
      {}
    ),
  recommendThemes: (siteId: string, params?: {industry?: string; style?: string; is_ecommerce?: boolean; is_b2b?: boolean}) =>
    api.post<Array<Record<string, unknown>>>(`/api/v1/sites/${siteId}/agent/themes/recommend`, params || {}),
  selectTheme: (siteId: string, body: {slug: string; name?: string; screenshot_url?: string}) =>
    api.post<{site_id: string; selected_theme: string; status: string}>(`/api/v1/sites/${siteId}/agent/themes/select`, body),
  syncWooCommerce: (siteId: string) =>
    api.post<{site_id: string; synced_count: number; status: string; message: string}>(`/api/v1/sites/${siteId}/agent/woocommerce/sync`, {}),
};

export const catalogApi = {
  ingest: (siteId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 120_000);
    return fetch(`/api/v1/sites/${siteId}/catalog/ingest`, {
      method: 'POST',
      headers: {Authorization: `Bearer ${localStorage.getItem('wsb_token') || ''}`},
      body: fd,
      signal: controller.signal,
    }).then(async (response) => {
      const text = await response.text();
      let data: unknown = null;
      if (text) {
        try { data = JSON.parse(text); } catch { data = text; }
      }
      if (!response.ok) {
        const body = data && typeof data === 'object' ? data as {detail?: unknown; message?: string} : {};
        const detail = body.detail;
        const detailObject = detail && typeof detail === 'object' ? detail as {message?: string; error_code?: string} : null;
        throw new ApiError(
          response.status,
          detailObject?.error_code || 'CATALOG_UPLOAD_FAILED',
          detailObject?.message || (typeof detail === 'string' ? detail : body.message) || '商品资料上传失败。',
          data,
        );
      }
      return data as CatalogIngestResponse;
    }).catch((reason: unknown) => {
      if (reason instanceof DOMException && reason.name === 'AbortError') {
        throw new Error('文件识别超过 2 分钟仍未完成，请检查文件大小或网络后重试。');
      }
      throw reason;
    }).finally(() => {
      window.clearTimeout(timeoutId);
    });
  },
  confirm: (
    siteId: string,
    jobId: string,
    body: {mapping: Record<string, string | string[] | null>},
  ) => api.post<CatalogIngestResponse>(`/api/v1/sites/${siteId}/catalog/ingest/${jobId}/confirm`, body),
  products: (siteId: string) =>
    api.get<CatalogProductsResponse>(`/api/v1/sites/${siteId}/catalog/products`),
};

// ===== Existing WJ Model Router management proxy =====
export const modelConfigApi = {
  get: () => api.get<ModelConfigResponse>('/api/v1/model-config'),
  configureActiveModel: (body: ActiveModelSetupRequest) =>
    api.post<ModelConfigResponse>('/api/v1/model-config/active-model', body),
  updateRuntime: (body: Partial<Omit<ModelRuntimeConfig, 'retry_label'>>) =>
    api.patch<ModelRuntimeConfig>('/api/v1/model-config/runtime', body),
  updateProvider: (id: number, body: {enabled?: boolean; timeout_ms?: number; concurrency?: number}) =>
    api.patch<{ok: boolean; provider_id: number; enabled: boolean}>(`/api/v1/model-config/providers/${id}`, body),
  testProvider: (id: number) =>
    api.post<{ok: boolean; latency_ms: number; status_code: number | null; message: string}>(
      `/api/v1/model-config/providers/${id}/test`,
      {},
    ),
  updateModel: (id: number, body: {enabled: boolean}) =>
    api.patch<{ok: boolean; model_id: number; enabled: boolean}>(`/api/v1/model-config/models/${id}`, body),
  updatePolicy: (name: string, body: {weights: Record<string, number>; enabled: boolean}) =>
    api.patch<{ok: boolean; name: string; weights: Record<string, number>; enabled: boolean}>(
      `/api/v1/model-config/policies/${name}`,
      body,
    ),
};
